import React, { useState, useEffect } from "react";
import {
  Col,
  Container,
  Row,
  FormGroup,
  Button,
  Card,
  Label,
} from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { post, put, get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { toast, Bounce } from "react-toastify";
import imgupload from "../../../assets/images/imageupload.svg";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { values } from "lodash";
import { stateToHTML } from "draft-js-export-html";

const AddFaq = () => {
  const [equipmentFiles, setEquipmentFiles] = useState({ files: [] });
  const [isUpdate, setIsUpdate] = useState(false);
  {
    isUpdate
      ? (document.title = "Edit FAQ | Elevated - Admin Dashboard")
      : (document.title = "Add FAQ | Elevated - Admin Dashboard");
  }
  const [blogId, setBlogId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: "",
    content: "",
    type: "faq",
  });
  const [image, setImage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const blogFromParams = searchParams.get("blogId");
    setBlogId(blogFromParams);

    if (blogFromParams) {
      setIsUpdate(true);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchBlogDetails = async (id) => {
      try {
        const response = await get(`${endpoints.help}/${id}`);
        if (response?.data) {
          const blogData = response.data.data;
          setInitialValues({
            title: blogData.title || "",
            content: blogData?.content || "",
            type: "faq",
          });
          const content = convertFromRaw(blogData?.content?.content);
          setEditorState(EditorState.createWithContent(content));
        } else {
          toast.error("No data found for the faq ID", {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          });
        }
      } catch (error) {
        console.error("Error fetching faq details:", error);
        toast.error("Failed to fetch faq details. Please try again.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      }
    };

    if (isUpdate && blogId) {
      fetchBlogDetails(blogId);
    }
  }, [isUpdate, blogId]);
  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Question is required"),
  });

  const handleSubmit = async (values) => {
    if (!values.title || !values.content) {
      toast.error("Both title and content are required.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      });
      return;
    }

    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("content", values.content);
    formData.append("type", "faq");

    // Debug FormData
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }

    try {
      setIsSubmitting(true);
      if (isUpdate) {
        await put(`${endpoints.help}/${blogId}`, formData);
        toast.success("FAQ Updated Successfully", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      } else {
        await post(endpoints.help, formData);
        toast.success("FAQ Added Successfully", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      }
      navigate("/list-faq");
    } catch (error) {
      console.error(`Error ${isUpdate ? "updating" : "adding"} Faq:`, error);
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // Handle editor content change and pass to Formik
  const handleEditorChange = (state, setFieldValue) => {
    setEditorState(state);
    // Convert the editor state to raw content
    const content = convertToRaw(state.getCurrentContent());
    const htmlContent = stateToHTML(state.getCurrentContent());
    console.log(JSON.stringify({ content, htmlContent }));
    setFieldValue("content", JSON.stringify({ content, htmlContent }));
  };
  return (
    <Container fluid>
      <Row>
        <Col>
          <Card className="rounded-4">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ errors, touched, resetForm, setFieldValue }) => (
                <Form className="p-4">
                  <h3 className="primary-black ff-sora font-size-20 fw-bold">
                    {isUpdate ? "Edit FAQ" : "Add New FAQ"}
                  </h3>

                  {/* General Info */}
                  <h5 className="secondary-black ff-sora mt-4 font-size-16 fw-normal">
                    Question <span className="text-danger">*</span>
                  </h5>
                  <Row>
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      lg={12}
                      className="me-lg-1 mb-1 mb-md-0"
                    >
                      <FormGroup>
                        <Field
                          name="title"
                          type="text"
                          placeholder="FAQ"
                          style={{
                            width: "100%",
                            maxWidth: "412px",
                            height: "56px",
                            border: "none",
                          }}
                          className={`rounded-5 bg-light ff-sora font-size-16 fw-normal px-4 ${
                            errors.title && touched.title ? " is-invalid" : ""
                          }`}
                        />
                        {errors.title && touched.title && (
                          <div className="text-danger font-size-12 ff-sora fw-normal my-1">
                            {errors.title}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <h5 className="secondary-black ff-sora mt-4 font-size-16 fw-normal">
                      Answer <span className="text-danger">*</span>
                    </h5>
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      lg={12}
                      className="me-lg-1 mb-1 mb-md-0"
                    >
                      <FormGroup>
                        <Editor
                          editorState={editorState}
                          toolbarClassName="toolbarClassName"
                          wrapperClassName="wrapperClassName"
                          editorClassName="editorClassName"
                          onEditorStateChange={(state) =>
                            handleEditorChange(state, setFieldValue)
                          }
                        />
                        {/* <Field
                          name="content"
                          type="text"
                          placeholder="Blog Description"
                          style={{
                            width: "100%",
                            maxWidth: "412px",
                            height: "56px",
                            border: "none",
                          }}
                          className={`rounded-5 bg-light ff-sora font-size-16 fw-normal px-4 ${
                            errors.content && touched.content
                              ? " is-invalid"
                              : ""
                          }`}
                        /> */}
                        {errors.content && touched.content && (
                          <div className="text-danger font-size-12 ff-sora fw-normal my-1">
                            {errors.content}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Submit Button */}
                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      type="submit"
                      className="me-2 rounded-pill"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        isUpdate ? (
                          <span>Updating...</span>
                        ) : (
                          <span>Adding...</span>
                        )
                      ) : isUpdate ? (
                        "Update FAQ"
                      ) : (
                        "Add FAQ"
                      )}
                    </Button>
                    <Button
                      color="white"
                      className="rounded-pill1"
                      onClick={() => {
                        resetForm();
                        if (isUpdate) {
                          navigate("/list-faq");
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              height: "60px",
              position: "sticky",
              bottom: "0",
              zIndex: "10",
            }}
          ></div>
        </Col>
      </Row>
    </Container>
  );
};

export default AddFaq;
